// 供應商發票 (應付帳款 AP)
import React, { useEffect, useState } from 'react';
import { api, money, fmtDate, todayStr, r2 } from '../api.js';
import { Modal, Field, Loading, Empty, PageHead, useToast, useConfirm } from '../ui.jsx';

function SIEDitor({ suppliers, pos, onClose, onSaved, initial }) {
  const { toast } = useToast();
  const isNew = !initial?.id;
  const [f, setF] = useState({
    supplier_id: initial?.supplier_id || '', po_id: initial?.po_id || '', order_id: initial?.order_id || '',
    supplier_inv_no: initial?.supplier_inv_no || '', si_date: initial?.si_date || todayStr(),
    amount: initial?.amount || '', notes: initial?.notes || '',
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const poSel = pos.find(p => p.id === Number(f.po_id));
  const save = async () => {
    if (!f.supplier_id) return toast('請選擇供應商', 'err');
    if (!(Number(f.amount) > 0)) return toast('請填寫金額', 'err');
    try {
      await api.post('/supplier-invoices', { ...f, amount: Number(f.amount), po_id: f.po_id || null, order_id: poSel?.order_id || f.order_id || null });
      toast('供應商發票已記錄 ✔'); onSaved();
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal onClose={onClose} icon="🚚" title="➕ 記錄供應商發票 (應付帳款)"
      footer={<><button className="btn" onClick={onClose}>取消</button><button className="btn primary" onClick={save}>💾 儲存</button></>}>
      <div className="form-grid">
        <Field label="供應商" required>
          <select className="select" value={f.supplier_id} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">— 選擇 —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="供應商發票號碼"><input className="input" value={f.supplier_inv_no} onChange={e => set('supplier_inv_no', e.target.value)} /></Field>
        <Field label="關聯採購單 (可選)">
          <select className="select" value={f.po_id} onChange={e => {
            const p = pos.find(x => x.id === Number(e.target.value));
            setF(x => ({ ...x, po_id: e.target.value, supplier_id: p ? p.supplier_id : x.supplier_id }));
          }}>
            <option value="">— 無 —</option>
            {pos.filter(p => !f.supplier_id || p.supplier_id === Number(f.supplier_id)).map(p => (
              <option key={p.id} value={p.id}>{p.po_no} · {p.order_no} · ${money(p.total)}</option>
            ))}
          </select>
        </Field>
        <Field label="發票日期"><input className="input" type="date" value={f.si_date} onChange={e => set('si_date', e.target.value)} /></Field>
        <Field label="金額 HK$" required><input className="input num-input" type="number" step="0.01" value={f.amount} onChange={e => set('amount', e.target.value)} /></Field>
        <Field label="備註"><input className="input" value={f.notes} onChange={e => set('notes', e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function PayModal({ si, onClose, done }) {
  const { toast } = useToast();
  const remain = r2(si.amount - si.paid_amount);
  const [f, setF] = useState({ pay_date: todayStr(), amount: remain, method: 'bank', reference: '' });
  const pay = async () => {
    try { await api.post(`/supplier-invoices/${si.id}/payments`, f); toast('已記錄付款 ✔'); done(); }
    catch (e) { toast(e.message, 'err'); }
  };
  const clear = async () => {
    try { await api.post(`/supplier-invoices/${si.id}/payments`, { ...f, amount: remain }); toast('已全數付清 ✔'); done(); }
    catch (e) { toast(e.message, 'err'); }
  };
  return (
    <Modal onClose={onClose} icon="💸" title={`付款俾供應商 — ${si.si_no}`}
      footer={<><button className="btn" onClick={onClose}>取消</button><button className="btn mint" onClick={clear}>✅ 全數付清</button><button className="btn primary" onClick={pay}>💾 記錄付款</button></>}>
      <div className="total-bar mb">
        <div className="line"><span>應付</span><span className="v">HK$ {money(si.amount)}</span></div>
        <div className="line"><span>已付</span><span className="v">HK$ {money(si.paid_amount)}</span></div>
        <div className="line grand"><span>未付</span><span className="v">HK$ {money(remain)}</span></div>
      </div>
      <div className="form-grid">
        <Field label="付款日期"><input className="input" type="date" value={f.pay_date} onChange={e => setF({ ...f, pay_date: e.target.value })} /></Field>
        <Field label="金額"><input className="input num-input" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label="方式">
          <select className="select" value={f.method} onChange={e => setF({ ...f, method: e.target.value })}>
            <option value="bank">銀行轉帳</option><option value="cheque">支票</option><option value="cash">現金</option>
          </select>
        </Field>
        <Field label="參考"><input className="input" value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

export default function SupplierInvoices() {
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [rows, setRows] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPOs] = useState([]);
  const [editor, setEditor] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const refresh = () => api.get(`/supplier-invoices${onlyOpen ? '?status=open' : ''}`).then(setRows);
  useEffect(() => {
    refresh();
    api.get('/suppliers').then(setSuppliers);
    api.get('/pos').then(setPOs);
  }, [onlyOpen]);
  const del = (si) => confirm(`刪除供應商發票 ${si.si_no} (連同付款紀錄)？`, async () => {
    try { await api.del(`/supplier-invoices/${si.id}`); toast('已刪除'); refresh(); } catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '刪除' });
  const totalAP = (rows || []).reduce((s, x) => s + (x.amount - x.paid_amount), 0);

  return (
    <div>
      <PageHead icon="🚚" title="供應商發票 (應付帳款)" desc={`記錄供應商開俾我哋嘅發票及已付金額 — 未付總額 HK$ ${money(totalAP)}`}
        actions={<>
          <div className="pill-filter">
            <button className={`pill ${onlyOpen ? 'active' : ''}`} onClick={() => setOnlyOpen(true)}>未付清</button>
            <button className={`pill ${!onlyOpen ? 'active' : ''}`} onClick={() => setOnlyOpen(false)}>全部</button>
          </div>
          <button className="btn primary" onClick={() => setEditor({})}>➕ 記錄發票</button>
        </>} />
      <div className="card">
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty icon="🚚" text="沒有供應商發票" /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>編號</th><th>供應商</th><th>供應商發票號</th><th>關聯 PO</th><th>日期</th><th className="num">金額</th><th className="num">已付</th><th className="num">未付</th><th></th></tr></thead>
              <tbody>
                {rows.map(si => {
                  const remain = r2(si.amount - si.paid_amount);
                  return (
                    <tr key={si.id}>
                      <td className="strong">{si.si_no}</td>
                      <td>{si.supplier_name}</td>
                      <td>{si.supplier_inv_no || '—'}</td>
                      <td className="sub">{si.po_no || '—'}</td>
                      <td>{fmtDate(si.si_date)}</td>
                      <td className="num strong">{money(si.amount)}</td>
                      <td className="num">{money(si.paid_amount)}</td>
                      <td className="num strong" style={{ color: remain > 0 ? 'var(--amber)' : 'var(--mint)' }}>{money(remain)}</td>
                      <td>
                        <div className="row" style={{ flexWrap: 'nowrap' }}>
                          {remain > 0 ? <button className="btn sm mint" onClick={() => setPayFor(si)}>💸 付款</button> : <span className="st st-green">付清</span>}
                          <button className="btn sm coral" onClick={() => del(si)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editor && <SIEDitor suppliers={suppliers} pos={pos} initial={editor.id ? editor : {}}
        onClose={() => setEditor(null)} onSaved={() => { setEditor(null); refresh(); }} />}
      {payFor && <PayModal si={payFor} onClose={() => setPayFor(null)} done={() => { setPayFor(null); refresh(); }} />}
      {confirmNode}
    </div>
  );
}
